<template>
  <div class="ps-table-wrap">
    <div class="table-content">
      <VeTable
        class="!pb-0"
        :fixed-header="true"
        :columns="columns"
        :table-data="tableData"
        :border-around="false"
        :sort-option="sortOption"
        :max-height="'calc(100vh - 250px)'"
      />
      <EmptyState
        v-if="!isLoading && showEmptyResult"
        :title="$t('AI.REPORT_COST.EMPTY_STATE.TITLE')"
      />
      <div v-if="isLoading" class="table-loading-overlay">
        <div class="table-loading-content">
          <Spinner />
          <span>{{ $t('COUPON.COUPON_LIST.LOADING') }}</span>
        </div>
      </div>
      <div v-if="pageTotal !== undefined" class="table-footer-row">
        <div class="footer-total-label">{{ $t('AI.REPORT_COST.TOTAL') }}:</div>
        <div class="footer-total-value">${{ pageTotal.toFixed(2) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';

export default {
  components: {
    VeTable,
    Spinner,
    EmptyState,
  },
  props: {
    records: {
      type: Array,
      default: () => [],
    },
    cols: {
      type: Array,
      default: () => [],
    },
    pageTotal: {
      type: Number,
      default: undefined,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortIcons: {
          ascend: 'top-arrow',
          descend: 'bottom-arrow',
        },
        sortChange: params => {
          this.sortConfig = params;
          this.$emit('on-sort-change', params);
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      meta: 'reportCosts/getReportCostMeta',
    }),
    showEmptyResult() {
      return this.tableData.length === 0;
    },
    tableData() {
      return this.records;
    },
    columns() {
      let matches = {};
      matches.name = {
        field: 'name',
        key: 'name',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.NAME'),
        width: 200,
        align: 'left',
        sortBy: this.sortConfig.name || '',
      };
      matches.model = {
        field: 'model',
        key: 'model',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.MODEL'),
        width: 180,
        align: 'left',
        sortBy: this.sortConfig.model || '',
      };
      matches.type = {
        field: 'type',
        key: 'type',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.TYPE'),
        width: 120,
        align: 'left',
        sortBy: this.sortConfig.type || '',
        renderBodyCell: ({ row }) => {
          const typeColors = {
            LLM: 'var(--s-500)',
            Vision: 'var(--w-600)',
            Audio: 'var(--b-500)',
            Embedding: 'var(--g-500)',
            Analytics: 'var(--w-600)',
          };
          return (
            <div class="type-badge">
              <span
                style={{
                  backgroundColor: typeColors[row.type] || 'var(--p-200)',
                }}
              >
                {this.$t(`AI.REPORT_COST.TYPE.${row.type.toUpperCase()}`) ||
                  row.type}
              </span>
            </div>
          );
        },
      };
      matches.cost_per_call = {
        field: 'cost_per_call',
        key: 'cost_per_call',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.COST_PER_CALL'),
        width: 120,
        align: 'left',
        sortBy: this.sortConfig.cost_per_call || '',
        renderBodyCell: ({ row }) => {
          return `$${row.cost_per_call.toFixed(4)}`;
        },
      };
      matches.total_calls = {
        field: 'total_calls',
        key: 'total_calls',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.TOTAL_CALLS'),
        width: 130,
        align: 'left',
        sortBy: this.sortConfig.total_calls || '',
        renderBodyCell: ({ row }) => {
          return row.total_calls.toLocaleString();
        },
      };
      matches.total_cost = {
        field: 'total_cost',
        key: 'total_cost',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.TOTAL_COST'),
        width: 130,
        align: 'left',
        sortBy: this.sortConfig.total_cost || '',
        renderBodyCell: ({ row }) => {
          return `$${row.total_cost.toFixed(2)}`;
        },
      };
      matches.last_used_at = {
        field: 'last_used_at',
        key: 'last_used_at',
        title: this.$t('AI.REPORT_COST.TABLE_HEADER.LAST_USED'),
        width: 200,
        align: 'left',
        sortBy: this.sortConfig.last_used_at || '',
        renderBodyCell: ({ row }) => {
          const date = new Date(row.last_used_at);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      };

      const preSet = [];
      this.cols.forEach(col => {
        if (col.visible) {
          if (col.key in matches) {
            preSet.push(matches[col.key]);
          }
        }
      });
      return preSet;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_ps_table.scss';

.table-content {
  position: relative;
}

.\!pb-0 {
  padding-bottom: 0 !important;
}

.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.table-loading-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.type-badge span {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  color: #fff;
  font-weight: 600;
  display: inline-block;
}

.type-badge {
  display: flex;
  align-items: center;
}

.status-badge span.inactive {
  color: var(--s-600);
}

.table-footer-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--s-200);
  background: var(--white);
}

.footer-total-label {
  font-weight: 600;
  color: var(--s-600);
  font-size: 14px;
}

.footer-total-value {
  font-weight: 700;
  color: var(--p-600);
  font-size: 14px;
}
</style>

<style lang="scss">
.ps-table-wrap {
  .ve-table-sort {
    display: inline-flex;
    flex-direction: column;
    margin-left: 4px;

    .ve-table-sort-icon {
      width: 14px !important;
      height: 14px !important;
      line-height: 1;

      svg {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }

  .fluent-icon {
    width: 14px !important;
    height: 14px !important;

    svg {
      width: 14px !important;
      height: 14px !important;
    }
  }
}
</style>
